import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputLabel,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  root: { position: 'relative' },
  dropdown: {
    position: 'absolute',
    zIndex: 1300,
    width: '100%',
    maxHeight: 280,
    overflow: 'auto',
  },
  listItem: {
    cursor: 'pointer',
    '&:hover': { background: theme.palette.action.hover },
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(0.5),
  },
  meta: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

interface GroupRow {
  machineId: string;
  title: string;
  type?: string;
}

export interface WorksOnBehalfOfPickerProps {
  value: string | undefined; // machine ID of the target group
  onChange: (machineId: string | undefined) => void;
  /** Machine ID of the team being edited — prevents self-selection */
  excludeMachineId?: string;
}

/**
 * Single-select search picker for a team's worksOnBehalfOf target. Unlike
 * ParentGroupPicker, the target is never a team — it must be at a strictly
 * higher hierarchy level (e.g. an XP or Function), matching the diagrammed
 * Team Data Model v2 example where the work-routing target crosses into a
 * different branch of the hierarchy entirely. Modelled on PersonnelPicker's
 * search-dropdown pattern, adapted for groups instead of users.
 */
export function WorksOnBehalfOfPicker({
  value,
  onChange,
  excludeMachineId,
}: WorksOnBehalfOfPickerProps) {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupRow | undefined>();

  // Resolve selected group display when value is pre-populated
  useEffect(() => {
    if (!value) {
      setSelectedGroup(undefined);
      return;
    }
    catalogApi
      .getEntityByRef(`group:default/${value}`)
      .then(entity => {
        if (!entity) return;
        const g = entity as GroupEntity;
        setSelectedGroup({
          machineId: value,
          title: g.metadata.title ?? g.metadata.name,
          type: g.spec?.type as string | undefined,
        });
      })
      .catch(() => {});
  }, [value, catalogApi]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setGroups([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const q = query.toLowerCase();
    catalogApi
      .getEntities({
        filter: { kind: 'Group' },
        fields: ['metadata.name', 'metadata.title', 'metadata.annotations', 'spec.type'],
      })
      .then(res => {
        const matched = (res.items as GroupEntity[])
          .filter(
            g =>
              g.metadata.annotations?.['backstage.io/lifecycle'] !== 'deprecated' &&
              g.metadata.name !== excludeMachineId &&
              // WOBO target must be at a higher level than the source — the
              // source here is always a team, so any other team is a peer
              // and never a valid target. Convenience filter only, not a
              // security boundary; the server enforces the real rule.
              g.spec?.type !== 'team',
          )
          .filter(g => {
            const title = (g.metadata.title ?? g.metadata.name).toLowerCase();
            return title.includes(q) || g.metadata.name.toLowerCase().includes(q);
          })
          .slice(0, 10)
          .map(g => ({
            machineId: g.metadata.name,
            title: g.metadata.title ?? g.metadata.name,
            type: g.spec?.type as string | undefined,
          }));
        setGroups(matched);
        setOpen(matched.length > 0);
      })
      .finally(() => setLoading(false));
  }, [query, catalogApi, excludeMachineId]);

  const handleSelect = (group: GroupRow) => {
    onChange(group.machineId);
    setSelectedGroup(group);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange(undefined);
    setSelectedGroup(undefined);
    setQuery('');
  };

  return (
    <Box className={classes.root}>
      {selectedGroup ? (
        <>
          <InputLabel shrink>Works On Behalf Of</InputLabel>
          <Box className={classes.selected}>
            <Box flex={1}>
              <Link to={`/catalog/default/group/${selectedGroup.machineId}`}>
                <Typography variant="body2">{selectedGroup.title}</Typography>
              </Link>
              {selectedGroup.type && (
                <Typography className={classes.meta}>{selectedGroup.type}</Typography>
              )}
            </Box>
            <IconButton size="small" onClick={handleClear}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        </>
      ) : (
        <TextField
          fullWidth
          size="small"
          label="Works On Behalf Of"
          placeholder="Search groups… (optional)"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => {
            if (groups.length > 0 && query.trim().length >= 2) setOpen(true);
          }}
          variant="outlined"
          InputProps={{
            endAdornment: loading ? <CircularProgress size={14} /> : undefined,
          }}
        />
      )}

      {open && groups.length > 0 && (
        <Paper className={classes.dropdown} elevation={4}>
          <List dense>
            {groups.map(group => (
              <ListItem
                key={group.machineId}
                className={classes.listItem}
                onClick={() => handleSelect(group)}
              >
                <ListItemText
                  primary={group.title}
                  secondary={group.type}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Typography variant="caption" color="textSecondary" display="block">
        Optional. Some teams sit under one Function structurally but deliver
        work for another part of the org. WOBO captures that without changing
        the primary hierarchy. The target must be a higher level, can't be in
        the team's own hierarchical line, and only one target is allowed.
      </Typography>
    </Box>
  );
}
